import React from 'react';
import { FolderOpen, Plus, Pencil, Trash2 } from 'lucide-react';
import type { FloorPlan } from '../types';

interface ProjectsPageProps {
  floorPlans: FloorPlan[];
  onNewFloorPlan: () => void;
  onOpenFloorPlan: (fp: FloorPlan) => void;
  onDeleteFloorPlan?: (fp: FloorPlan) => void;
  header: React.ReactNode;
}

const ProjectsPage: React.FC<ProjectsPageProps> = ({
  floorPlans,
  onNewFloorPlan,
  onOpenFloorPlan,
  onDeleteFloorPlan,
  header,
}) => {
  return (
    <div className="min-h-screen flex flex-col" style={{ backgroundColor: '#111118' }}>
      {header}
      <div className="flex-1 px-4 py-8 max-w-4xl mx-auto w-full">
        <div className="flex items-center justify-between mb-8">
          <h2 className="text-xl font-bold" style={{ color: '#f0f0f5' }}>
            Your floor plans
          </h2>
          <button
            onClick={onNewFloorPlan}
            className="flex items-center gap-2 px-4 py-2 rounded-lg font-medium"
            style={{ backgroundColor: '#FF4C4C', color: '#fff' }}
          >
            <Plus size={18} />
            New Floor Plan
          </button>
        </div>

        {floorPlans.length === 0 ? (
          <div
            className="rounded-2xl border-2 border-dashed p-12 text-center"
            style={{ borderColor: '#2e2e40' }}
          >
            <FolderOpen size={48} className="mx-auto mb-4" style={{ color: '#8888aa' }} />
            <p className="text-lg font-medium mb-2" style={{ color: '#f0f0f5' }}>
              No floor plans yet
            </p>
            <p className="text-sm mb-6" style={{ color: '#8888aa' }}>
              Create your first floor plan to get started
            </p>
            <button
              onClick={onNewFloorPlan}
              className="px-6 py-2.5 rounded-lg font-medium"
              style={{ backgroundColor: '#FF4C4C', color: '#fff' }}
            >
              New Floor Plan
            </button>
          </div>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {floorPlans.map((fp) => (
              <div
                key={fp.id}
                className="rounded-xl border overflow-hidden group transition-all hover:border-[#FF4C4C]/50"
                style={{ backgroundColor: '#1c1c28', borderColor: '#2e2e40' }}
              >
                <div
                  className="aspect-video flex items-center justify-center"
                  style={{ backgroundColor: '#111118' }}
                >
                  {fp.thumbnail_url ? (
                    <img src={fp.thumbnail_url} alt="" className="w-full h-full object-contain" />
                  ) : (
                    <FolderOpen size={48} style={{ color: '#2e2e40' }} />
                  )}
                </div>
                <div className="p-3 flex items-center justify-between">
                  <h3 className="font-medium truncate flex-1" style={{ color: '#f0f0f5' }}>
                    {fp.name}
                  </h3>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={(e) => { e.stopPropagation(); onOpenFloorPlan(fp); }}
                      className="p-1.5 rounded-lg hover:bg-white/5"
                      style={{ color: '#8888aa' }}
                      title="Edit"
                    >
                      <Pencil size={16} />
                    </button>
                    {onDeleteFloorPlan && (
                      <button
                        onClick={(e) => { e.stopPropagation(); onDeleteFloorPlan(fp); }}
                        className="p-1.5 rounded-lg hover:bg-red-500/20"
                        style={{ color: '#8888aa' }}
                        title="Delete"
                      >
                        <Trash2 size={16} />
                      </button>
                    )}
                  </div>
                </div>
                <button
                  onClick={() => onOpenFloorPlan(fp)}
                  className="w-full py-2 text-sm font-medium"
                  style={{ color: '#FF4C4C', borderTop: '1px solid #2e2e40' }}
                >
                  Open
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ProjectsPage;
